import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Pricing from "@/pages/Pricing";
import Legal from "@/pages/Legal";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCanceled from "@/pages/PaymentCanceled";
import Dashboard from "@/pages/Dashboard";
import Absences from "@/pages/Absences";
import AdminOverview from "@/pages/AdminOverview";
import AdminRequests from "@/pages/AdminRequests";
import AdminOperators from "@/pages/AdminOperators";
import AdminRecords from "@/pages/AdminRecords";
import AdminSchedules from "@/pages/AdminSchedules";
import Timesheet from "@/pages/Timesheet";
import AdminBilling from "@/pages/AdminBilling";
import AdminSettings from "@/pages/AdminSettings";
import Quote from "@/pages/Quote";
import Onboarding from "@/pages/Onboarding";
import AdminAudit from "@/pages/AdminAudit";
import { CookieNotice } from "@/components/CookieNotice";

const Guard = ({ children, ownerOnly }) => {
  const { user, loading, pharmacy, logout } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-10 text-gray-600">Caricamento...</div>;
  if (!user) return <Navigate to="/" replace />;
  if (ownerOnly && user.role !== "owner") return <Navigate to="/dashboard" replace />;
  if (pharmacy?.access_enabled === false && location.pathname !== "/admin/fatturazione") {
    if (user.role === "owner") return <Navigate to="/attiva" replace />;
    return <div data-testid="operator-subscription-blocked" className="p-8"><p>Il titolare deve riattivare l'abbonamento della farmacia.</p><button data-testid="blocked-logout" onClick={() => logout()} className="mt-4 underline">Esci</button></div>;
  }
  return <Layout>{children}</Layout>;
};

const RootEntry = () => {
  const { user, loading, pharmacy } = useAuth();
  if (loading) return <div className="p-10 text-gray-600">Caricamento...</div>;
  if (user) return <Navigate to={user.role === "owner" ? (pharmacy?.access_enabled === false ? "/attiva" : "/admin") : "/dashboard"} replace />;
  return <Landing />;
};

const LoginEntry = () => {
  const { user, loading, pharmacy } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-10 text-gray-600">Caricamento...</div>;
  if (user) return <Navigate to={user.role === "owner" ? (pharmacy?.access_enabled === false ? `/attiva${location.search}` : "/admin") : "/dashboard"} replace />;
  return <Login />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <CookieNotice />
        <Routes>
          <Route path="/" element={<RootEntry />} />
          <Route path="/accedi" element={<LoginEntry />} />
          <Route path="/prezzi" element={<Pricing />} />
          <Route path="/preventivo" element={<Quote />} />
          <Route path="/attiva" element={<Onboarding />} />
          <Route path="/privacy" element={<Legal />} />
          <Route path="/cookie-policy" element={<Legal />} />
          <Route path="/termini" element={<Legal />} />
          <Route path="/pagamento/successo" element={<PaymentSuccess />} />
          <Route path="/pagamento/annullato" element={<PaymentCanceled />} />
          <Route path="/dashboard" element={<Guard><Dashboard /></Guard>} />
          <Route path="/assenze" element={<Guard><Absences /></Guard>} />
          <Route path="/admin" element={<Guard ownerOnly><AdminOverview /></Guard>} />
          <Route path="/admin/richieste" element={<Guard ownerOnly><AdminRequests /></Guard>} />
          <Route path="/admin/timbrature" element={<Guard ownerOnly><AdminRecords /></Guard>} />
          <Route path="/admin/report" element={<Guard ownerOnly><Timesheet /></Guard>} />
          <Route path="/cartellino" element={<Guard><Timesheet /></Guard>} />
          <Route path="/admin/turni" element={<Guard ownerOnly><AdminSchedules /></Guard>} />
          <Route path="/admin/operatori" element={<Guard ownerOnly><AdminOperators /></Guard>} />
          <Route path="/admin/fatturazione" element={<Guard ownerOnly><AdminBilling /></Guard>} />
          <Route path="/admin/impostazioni" element={<Guard ownerOnly><AdminSettings /></Guard>} />
          <Route path="/admin/registro" element={<Guard ownerOnly><AdminAudit /></Guard>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
